import classNames from "classnames";
import React, { useState, useEffect, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { Dropdown, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import {
  Bell,
  BellOff,
  CalendarCheck,
  CheckCheck,
  LifeBuoy,
  MessageSquareWarning,
  Receipt,
  Vote,
  Wallet,
} from "lucide-react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  fetchNotifications,
  markNotificationsAsRead,
} from "../../helpers/api/notifications";
import { NotificationContext } from "../../context/Context";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import {
  getRewardsNotificationView,
  isRewardsNotification,
  parseNotificationData,
} from "../rewards/rewardsNotifications";
import { REWARDS_ENABLED } from "../../helpers/rewardsEnabled";

// Icono y color de cada tipo de notificación (los colores son los de
// .hl-icon-tile--*). Las de Homelan Rewards usan su emoji.
const TYPE_STYLES = {
  voting: { icon: Vote, tone: "info" },
  "new-complaint": { icon: MessageSquareWarning, tone: "warning" },
  "complaint-closed": { icon: MessageSquareWarning, tone: "success" },
  "reserved-common-area": { icon: CalendarCheck, tone: "success" },
  "new-debts-to-pay": { icon: Receipt, tone: "warning" },
  "property-payment": { icon: Wallet, tone: "success" },
  "new-ticket": { icon: LifeBuoy, tone: "primary" },
  "response-ticket": { icon: LifeBuoy, tone: "primary" },
};
const DEFAULT_STYLE = { icon: Bell, tone: "secondary" };

// Los mensajes vienen del backend como texto; por si alguno trae etiquetas,
// se quitan en vez de inyectar HTML en el menú.
const plainText = (value) => String(value ?? "").replace(/<[^>]*>/g, "").trim();

// Texto de la notificación. Muchos mensajes ya incluyen el título ("Se ha
// creado un nuevo ticket con el nombre de X"): solo se agrega si no está.
const messageOf = (data) => {
  const message = plainText(data?.message);
  const title = plainText(data?.data?.title);
  if (!title || message.includes(title)) return message || title;
  if (!message) return title;
  // "Nueva encuesta disponible:" ya presenta el título; si no, se separa.
  return /[:.!?]$/.test(message) ? `${message} ${title}` : `${message} · ${title}`;
};

// A qué id y con qué parámetros navega cada tipo al abrir la notificación.
const targetOf = (type, data) => {
  const typeId =
    type === "reserved-common-area" ? data?.data?.common_area?.id : data?.data?.id;
  switch (type) {
    case "new-debts-to-pay":
      return {
        typeId,
        params: {
          amount: data?.data?.amount,
          date: data?.data?.created_at,
          propertyDebtId: data?.data?.id,
          aliquot: data?.data?.aliquot,
          paymented: false,
          total_paid: 0,
        },
      };
    case "property-payment":
      return {
        typeId,
        params: {
          condominium: data?.data?.property_debts?.property?.condominium?.id,
          id: data?.data?.id,
        },
      };
    case "new-ticket":
      return { typeId, params: { id: data?.data?.id } };
    case "response-ticket":
      return { typeId, params: { id: data?.data?.ticket_id } };
    default:
      return { typeId, params: null };
  }
};

const NotificationItem = ({ item, onOpen }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={classNames("hl-notification", { "is-unread": item.unread })}
      onClick={onOpen}
    >
      <span
        className={`hl-icon-tile hl-icon-tile--sm hl-icon-tile--${item.tone} hl-notification__icon`}
        aria-hidden="true"
      >
        {item.emoji ? (
          <span className="hl-notification__emoji">{item.emoji}</span>
        ) : (
          <Icon size={16} strokeWidth={2} />
        )}
      </span>
      <span className="hl-notification__body">
        {item.tag && <span className="hl-notification__tag">{item.tag}</span>}
        <span className="hl-notification__text">{item.text}</span>
        <span className="hl-notification__time">{item.time}</span>
      </span>
      {item.unread && (
        <span className="hl-notification__dot">
          <span className="sr-only">Sin leer</span>
        </span>
      )}
    </button>
  );
};

const NotificationDropdown = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, setNotifications } = useContext(NotificationContext);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const handleToggle = (e) => {
    e?.preventDefault();
    setIsOpen(!isOpen);
  };

  const findNotifications = () => {
    setLoading(true);
    fetchNotifications()
      .then((response) => {
        const { data } = response;
        setNotifications(data);
      })
      .catch((error) => {
        handleErrorMessage(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    findNotifications();
  }, []);

  const markAsReadAll = () => {
    setSubmitting(true);
    markNotificationsAsRead()
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          findNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const markAsRead = (type, id, typeId, params = null) => {
    setSubmitting(true);
    markNotificationsAsRead(id)
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.OK) {
          findNotifications();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSubmitting(false);
        history.push({
          // HO-TASK-290: las de Homelan Rewards llevan a la ruta que define
          // su tipo (la vista de Rewards o, si la racha está en riesgo, a pagar).
          pathname: isRewardsNotification(type)
            ? params?.route
            : type === "voting"
              ? `/dashboard/surveys/${typeId}`
              : type === "new-complaint" || type === "complaint-closed"
              ? `/dashboard/complaints/${typeId}`
              : type === "reserved-common-area"
              ? `/dashboard/areas/${typeId}/reservations`
              : type === "new-debts-to-pay"
              ? `/dashboard/my/finance/topay/${typeId}`
              : type === "property-payment"
              ? `/dashboard/finance/${params?.condominium}/payments/${params?.id}`
              : type === "new-ticket"
              ? `/root/support/${params?.id}`
              : type === "response-ticket"
              ? `/dashboard/support/${params?.id}`
              : "",
          state: type === "new-debts-to-pay" ? params : null,
        });
      });
  };

  const items = (Array.isArray(notifications) ? notifications : [])
    .filter((n) => REWARDS_ENABLED || !isRewardsNotification(n.type))
    .map((notification) => {
      const base = {
        key: notification.id,
        unread: !notification.read_at,
        time: moment(notification.created_at).fromNow(),
      };
      const rewards = getRewardsNotificationView(notification);
      if (rewards) {
        return {
          ...base,
          emoji: rewards.emoji,
          tone: "primary",
          tag: `Homelan Rewards · ${rewards.label}`,
          text: plainText(rewards.message),
          open: () =>
            markAsRead(notification.type, notification.id, null, {
              route: rewards.route,
            }),
        };
      }
      const data = parseNotificationData(notification);
      const { typeId, params } = targetOf(notification.type, data);
      return {
        ...base,
        ...(TYPE_STYLES[notification.type] || DEFAULT_STYLE),
        text: messageOf(data),
        open: () => markAsRead(notification.type, notification.id, typeId, params),
      };
    });

  const unread = items.filter((item) => item.unread);
  const read = items.filter((item) => !item.unread);
  const groups = [
    { key: "unread", label: "Nuevas", items: unread },
    { key: "read", label: "Anteriores", items: read },
  ].filter((group) => group.items.length > 0);

  const openItem = (item) => {
    setIsOpen(false);
    item.open();
  };

  return (
    <Dropdown
      nav
      inNavbar
      isOpen={isOpen}
      toggle={handleToggle}
      onMouseOver={() => {
        const windowWidth = window.innerWidth;
        windowWidth > 992 && setIsOpen(true);
      }}
      onMouseLeave={() => {
        const windowWidth = window.innerWidth;
        windowWidth > 992 && setIsOpen(false);
      }}
    >
      <DropdownToggle
        nav
        className={classNames("px-0", {
          "notification-indicator notification-indicator-primary": unread.length > 0,
        })}
        aria-label={
          unread.length > 0
            ? `Notificaciones, ${unread.length} sin leer`
            : "Notificaciones"
        }
      >
        <span className="hl-icon-button">
          <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
        </span>
      </DropdownToggle>
      <DropdownMenu right className="dropdown-menu-card hl-notifications">
        <div className="hl-notifications__header">
          <div className="d-flex align-items-center">
            <h6 className="hl-notifications__title">Notificaciones</h6>
            {unread.length > 0 && (
              <span className="hl-notifications__count">{unread.length}</span>
            )}
          </div>
          {unread.length > 0 && (
            <button
              type="button"
              className="hl-notifications__mark"
              onClick={markAsReadAll}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <CheckCheck size={14} strokeWidth={2.25} aria-hidden="true" />
              )}
              Marcar como leídas
            </button>
          )}
        </div>

        <div className="hl-notifications__list">
          {loading && items.length === 0 ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : groups.length > 0 ? (
            groups.map((group) => (
              <div key={group.key} role="group" aria-label={group.label}>
                <p className="hl-notifications__group">{group.label}</p>
                {group.items.map(({ key, ...item }) => (
                  <NotificationItem key={key} item={item} onOpen={() => openItem(item)} />
                ))}
              </div>
            ))
          ) : (
            <div className="hl-notifications__empty">
              <span className="hl-icon-tile hl-icon-tile--neutral" aria-hidden="true">
                <BellOff size={18} strokeWidth={1.75} />
              </span>
              <p className="hl-notifications__empty-title">Estás al día</p>
              <p className="hl-notifications__empty-text">
                Aquí verás los pagos, reservas, tickets y avisos de tu condominio.
              </p>
            </div>
          )}
        </div>

        <Link
          className="hl-notifications__footer"
          to={user?.role !== "root" ? "/dashboard/notifications" : "/root/notifications"}
          onClick={() => setIsOpen(false)}
        >
          Ver todas las notificaciones
        </Link>
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationDropdown;
